// @file Helper for grouping posts
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { postColorText, POST_COLOR_STRINGS } from '@@/bits/post_color'
import { WallWishGroupByTypes, WallWishGroupByValue } from '@@/enums'
import type {
    HashId,
    Post,
    PostColor,
    PostColorId,
    PostCustomProperties,
    WallCustomPostSingleSelectProperty,
    WallGroupNone,
    WallWishGroupBy,
    WallWishGroupByCustomProp
} from '@@/types'

interface PostGroupingInfo {
  groupBy: WallWishGroupBy
  selectedGroupId: HashId | PostColorId | undefined
}

interface GroupingParams {
  wallSectionHashid?: HashId
  color?: PostColor | null
  customProperties?: PostCustomProperties
}

type GroupByNone = Lowercase<WallWishGroupByTypes.None>
interface Group {
  id: string | number
  [k: string]: any
}

const BLANK_GROUP: WallGroupNone = { id: WallWishGroupByTypes.None }

const ORDERED_COLOR_GROUPS = POST_COLOR_STRINGS.map((c) => {
  return {
    id: c,
  }
})

const getOrderedColorGroupsWithTitle = ({ isLightColorScheme }: { isLightColorScheme: boolean }) => {
  return ORDERED_COLOR_GROUPS.map((color: { id: PostColorId }) => ({
    id: color.id,
    title: postColorText({
      postColor: color.id,
      isLightColorScheme,
    }),
  }))
}

const isGroupByCustomProp = (groupBy: WallWishGroupBy): groupBy is WallWishGroupByCustomProp => {
  return groupBy.startsWith('custom_prop:')
}

const getCustomPropIdFromWishGroupBy = (groupBy: WallWishGroupBy): WallCustomPostSingleSelectProperty['id'] | null => {
  if (!isGroupByCustomProp(groupBy)) return null
  return groupBy.split(':')[1]
}

// Helper function to filter posts by color
const filterPostsByColor = (posts: Post[], colorValue: PostColorId): Post[] => {
  return posts.filter((post) => (post.color == null ? colorValue === 'default' : colorValue === post.color))
}

// Helper function to filter posts by generic property
const filterPostsByProperty = (posts: Post[], propertyKey: keyof Post, value: string | number): Post[] => {
  if (propertyKey === 'author_hashid') {
    return posts.filter((post) => {
      if (!post.author_hashid) return false
      const postAuthorHashidWithoutPrefix = removeHashidPrefix(post.author_hashid)
      return postAuthorHashidWithoutPrefix === value
    })
  }
  return posts.filter((post) => String(post[propertyKey]) === String(value))
}

const getPostsByGroupByProperty = <T extends Group['id']>(
  posts: Post[],
  sortedGroupByValues: T[],
  groupBy: WallWishGroupBy,
): { [K in T | GroupByNone]?: Post[] } => {
  if (groupBy === WallWishGroupByTypes.None) {
    return {
      none: posts,
    }
  }

  return sortedGroupByValues.reduce<{ [K in T | GroupByNone]?: Post[] }>((obj, groupByValue) => {
    let filteredPosts: Post[]

    switch (groupBy) {
      case WallWishGroupByTypes.Color:
        filteredPosts = filterPostsByColor(posts, groupByValue as PostColorId)
        break
      case WallWishGroupByTypes.Section:
        filteredPosts = filterPostsByProperty(posts, 'wall_section_id', groupByValue)
        break
      case WallWishGroupByTypes.Author:
        filteredPosts = filterPostsByProperty(posts, 'author_hashid', groupByValue)
        break
      default:
        // Group by single select custom property
        filteredPosts = posts.filter((post) => {
          const singleSelectId = getCustomPropIdFromWishGroupBy(groupBy)
          if (singleSelectId == null) return false

          const customPostPropertyValue = post.custom_properties?.[singleSelectId]

          // Posts with no value should be grouped with the "No value" group
          if (customPostPropertyValue == null) return groupByValue === WallWishGroupByValue.NoValue

          return customPostPropertyValue === groupByValue
        })
        break
    }

    return {
      ...obj,
      [groupByValue]: filteredPosts,
    }
  }, {})
}

/**
 * Helper function to return grouping parameters for WishApi.transfer and WishApi.copy.
 */
const getPostGroupingParams = (groupingInfo?: PostGroupingInfo): GroupingParams => {
  const params: GroupingParams = {}

  if (!groupingInfo) return params

  if (groupingInfo.groupBy === WallWishGroupByTypes.Section) {
    params.wallSectionHashid = groupingInfo.selectedGroupId as HashId
  } else if (groupingInfo.groupBy === WallWishGroupByTypes.Color) {
    params.color = groupingInfo.selectedGroupId === 'default' ? null : (groupingInfo.selectedGroupId as PostColor)
  } else if (isGroupByCustomProp(groupingInfo.groupBy)) {
    const propertyId = getCustomPropIdFromWishGroupBy(groupingInfo.groupBy)
    if (propertyId) {
      const customProperties: PostCustomProperties = {}
      // Handle "No value" selection by setting property to undefined, otherwise set to selected value
      customProperties[propertyId] =
        groupingInfo.selectedGroupId === WallWishGroupByValue.NoValue ? null : groupingInfo.selectedGroupId
      params.customProperties = customProperties
    }
  }

  return params
}

export {
    BLANK_GROUP,
    getCustomPropIdFromWishGroupBy,
    getOrderedColorGroupsWithTitle,
    getPostGroupingParams,
    getPostsByGroupByProperty,
    isGroupByCustomProp,
    ORDERED_COLOR_GROUPS,
}
export type { Group }
export type { PostGroupingInfo }
